import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  useAdminReports,
  useResolveReport,
  useDismissReport,
  useBulkDismissReports,
  useBulkResolveReports,
} from '../../hooks/use-admin';
import {
  MessageSquareWarning,
  Search,
  Filter,
  AlertTriangle,
  CheckCircle,
  XCircle,
  MoreVertical,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Clock,
  Flag,
  Eye,
  Image,
  Video,
  FileText,
  User,
  Camera,
  Trash2,
  CheckSquare,
  Square,
  TrendingUp,
  Timer,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { cn } from '../../lib/utils';
import { useToast } from '../../hooks/use-toast';

const REPORT_REASONS = {
  spam: { label: 'Spam', color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
  harassment: {
    label: 'Harassment',
    color: 'text-red-400',
    bg: 'bg-red-500/20',
  },
  hate_speech: {
    label: 'Hate Speech',
    color: 'text-red-500',
    bg: 'bg-red-600/20',
  },
  misinformation: {
    label: 'Misinformation',
    color: 'text-orange-400',
    bg: 'bg-orange-500/20',
  },
  inappropriate: {
    label: 'Inappropriate',
    color: 'text-purple-400',
    bg: 'bg-purple-500/20',
  },
  copyright: {
    label: 'Copyright',
    color: 'text-blue-400',
    bg: 'bg-blue-500/20',
  },
  scam: { label: 'Scam', color: 'text-red-400', bg: 'bg-red-500/20' },
  fraudulent: {
    label: 'Fraudulent',
    color: 'text-purple-400',
    bg: 'bg-purple-500/20',
  },
  stolen: {
    label: 'Stolen Content',
    color: 'text-indigo-400',
    bg: 'bg-indigo-500/20',
  },
  pornographic: {
    label: 'Pornographic',
    color: 'text-pink-400',
    bg: 'bg-pink-500/20',
  },
  violent: { label: 'Violent', color: 'text-red-500', bg: 'bg-red-600/20' },
  csam: { label: 'CSAM', color: 'text-red-600', bg: 'bg-red-700/20' },
  illegal: {
    label: 'Illegal Content',
    color: 'text-red-600',
    bg: 'bg-red-700/20',
  },
  doxxing: {
    label: 'Doxxing',
    color: 'text-orange-500',
    bg: 'bg-orange-600/20',
  },
  self_harm_promotion: {
    label: 'Self Harm',
    color: 'text-red-500',
    bg: 'bg-red-600/20',
  },
  nsfw: { label: 'NSFW', color: 'text-pink-400', bg: 'bg-pink-500/20' },
  other: { label: 'Other', color: 'text-zinc-400', bg: 'bg-zinc-500/20' },
};

const ENTITY_TYPE_ICONS = {
  discussion: FileText,
  comment: MessageSquareWarning,
  particle: FileText,
  user: User,
  image: Image,
  video: Video,
  media: Camera,
  profile_image: User,
  banner: Image,
  thumbnail: Image,
};

const PRIORITY_BADGES = {
  low: {
    label: 'Low',
    className: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
  },
  medium: {
    label: 'Medium',
    className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  },
  high: {
    label: 'High',
    className: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  },
  critical: {
    label: 'Critical',
    className: 'bg-red-500/20 text-red-400 border-red-500/30',
  },
};

function ReportRow({ report, onAction, isSelected, onSelect }) {
  const reasonInfo = REPORT_REASONS[report.reason] || REPORT_REASONS.other;
  const priorityInfo =
    PRIORITY_BADGES[report.priority] || PRIORITY_BADGES.medium;
  const EntityIcon = ENTITY_TYPE_ICONS[report.entityType] || Flag;
  const canSelect = report.status === 'pending' || report.status === 'reviewing';

  const isMediaReport = [
    'image',
    'video',
    'media',
    'profile_image',
    'banner',
    'thumbnail',
  ].includes(report.entityType);

  const RESOLUTION_LABELS = {
    no_action: 'No Action Taken',
    warning_issued: 'Warning Issued',
    content_removed: 'Content Removed',
    user_suspended: 'User Suspended',
    user_banned: 'User Banned',
  };

  const statusBadge = () => {
    if (report.status === 'resolved') {
      return (
        <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
          Resolved
        </Badge>
      );
    }
    if (report.status === 'dismissed') {
      return (
        <Badge className="bg-zinc-500/20 text-zinc-400 border-zinc-500/30">
          Dismissed
        </Badge>
      );
    }
    if (report.status === 'reviewing') {
      return (
        <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
          Reviewing
        </Badge>
      );
    }
    return (
      <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
        Pending
      </Badge>
    );
  };

  const getEntityLink = () => {
    if (report.entityType === 'discussion') {
      return `/forum/${report.entityOID || report.entityId}`;
    }
    if (report.entityType === 'user') {
      return `/profile/${report.reportedUserWallet}`;
    }
    if (isMediaReport && report.contentSnapshot?.mediaUrl) {
      return report.contentSnapshot.mediaUrl;
    }
    return '#';
  };

  return (
    <div className="flex items-start justify-between p-4 border-b border-zinc-800 hover:bg-zinc-900/50 transition-colors">
      {/* Selection Checkbox */}
      {canSelect && (
        <div className="mr-4 mt-1">
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => onSelect(report._id)}
            className="border-zinc-600 data-[state=checked]:bg-blue-500"
          />
        </div>
      )}
      {!canSelect && <div className="w-6 mr-4" />}

      {/* Media Preview for image/video reports */}
      {isMediaReport && report.contentSnapshot?.mediaUrl && (
        <div className="w-16 h-16 rounded-lg overflow-hidden bg-zinc-800 mr-4 flex-shrink-0">
          {report.entityType === 'video' ? (
            <div className="w-full h-full flex items-center justify-center">
              <Video className="h-8 w-8 text-zinc-500" />
            </div>
          ) : (
            <img
              src={report.contentSnapshot.mediaUrl}
              alt="Reported media"
              loading="lazy"
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          )}
        </div>
      )}

      <div className="flex-1 min-w-0 mr-4">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <Badge variant="outline" className={priorityInfo.className}>
            {priorityInfo.label}
          </Badge>
          <Badge className={cn(reasonInfo.bg, reasonInfo.color, 'border-0')}>
            {reasonInfo.label}
          </Badge>
          {statusBadge()}
          {/* Community Badge */}
          {report.community && (
            <Link
              to={`/community/${report.community.slug}`}
              className="flex items-center gap-1 text-xs bg-indigo-500/20 text-indigo-400 px-2 py-0.5 rounded-full hover:bg-indigo-500/30 transition-colors"
            >
              {report.community.profileImage && (
                <img
                  src={report.community.profileImage}
                  alt=""
                  className="w-3 h-3 rounded-full"
                />
              )}
              {report.community.name}
            </Link>
          )}
        </div>

        <div className="text-sm text-zinc-300 mb-2 flex items-center gap-2">
          <EntityIcon className="h-4 w-4 text-zinc-500" />
          <span className="text-zinc-500">Reported: </span>
          <span className="capitalize">
            {report.entityType?.replace(/_/g, ' ')}
          </span>
          {report.contentSnapshot?.title && (
            <span className="text-white font-medium ml-1">
              "{report.contentSnapshot.title}"
            </span>
          )}
        </div>

        {report.description && (
          <p className="text-sm text-zinc-400 line-clamp-2 mb-2">
            {report.description}
          </p>
        )}

        <div className="flex items-center gap-4 text-xs text-zinc-500 flex-wrap">
          <span className="flex items-center gap-1">
            <Flag className="h-3 w-3" />
            Reported by:{' '}
            {report.reporterUsername ||
              report.reporterWallet?.slice(0, 8) + '...'}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {new Date(report.createdAt).toLocaleDateString()}
          </span>
          {isMediaReport && report.contentSnapshot?.mimeType && (
            <span className="text-zinc-600">
              {report.contentSnapshot.mimeType}
            </span>
          )}
        </div>

        {/* Resolution Info - show if resolved or dismissed */}
        {(report.status === 'resolved' || report.status === 'dismissed') && (
          <div className="mt-3 pt-3 border-t border-zinc-800">
            <div className="flex items-center gap-4 text-xs flex-wrap">
              {report.resolution && (
                <span className="flex items-center gap-1 text-green-400">
                  <CheckCircle className="h-3 w-3" />
                  Action:{' '}
                  {RESOLUTION_LABELS[report.resolution] || report.resolution}
                </span>
              )}
              {report.resolvedByUsername && (
                <span className="text-zinc-500">
                  By: {report.resolvedByUsername}
                </span>
              )}
              {report.resolvedAt && (
                <span className="text-zinc-600">
                  {new Date(report.resolvedAt).toLocaleDateString()}
                </span>
              )}
            </div>
            {report.resolutionNotes && (
              <p className="text-xs text-zinc-500 mt-1 line-clamp-1">
                Notes: {report.resolutionNotes}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-zinc-400 hover:text-white"
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="bg-zinc-900 border-zinc-800"
          >
            <DropdownMenuItem
              className="text-zinc-300 focus:bg-zinc-800 cursor-pointer"
              onClick={() => onAction('view', report)}
            >
              <Eye className="h-4 w-4 mr-2" />
              View Details
            </DropdownMenuItem>
            {isMediaReport && report.contentSnapshot?.mediaUrl ? (
              <DropdownMenuItem
                className="text-zinc-300 focus:bg-zinc-800 cursor-pointer"
                onClick={() =>
                  window.open(report.contentSnapshot.mediaUrl, '_blank')
                }
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                View Media
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                asChild
                className="text-zinc-300 focus:bg-zinc-800 cursor-pointer"
              >
                <Link to={getEntityLink()} target="_blank">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View Reported Content
                </Link>
              </DropdownMenuItem>
            )}
            {report.status === 'pending' || report.status === 'reviewing' ? (
              <>
                <DropdownMenuSeparator className="bg-zinc-800" />
                <DropdownMenuItem
                  className="text-green-400 focus:bg-zinc-800 cursor-pointer"
                  onClick={() => onAction('resolve', report)}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Resolve Report
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-zinc-400 focus:bg-zinc-800 cursor-pointer"
                  onClick={() => onAction('dismiss', report)}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Dismiss Report
                </DropdownMenuItem>
              </>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

export default function AdminReports() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedReport, setSelectedReport] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [resolution, setResolution] = useState('');
  const [actionTaken, setActionTaken] = useState('warning_issued');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkActionType, setBulkActionType] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { toast } = useToast();

  const currentPage = parseInt(searchParams.get('page') || '1');
  const statusFilter = searchParams.get('status') || 'all';
  const reasonFilter = searchParams.get('reason') || 'all';
  const priorityFilter = searchParams.get('priority') || 'all';
  const communityFilter = searchParams.get('communityOid') || '';

  const { data, isLoading, error, refetch } = useAdminReports({
    page: currentPage,
    limit: 20,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    reason: reasonFilter !== 'all' ? reasonFilter : undefined,
    priority: priorityFilter !== 'all' ? priorityFilter : undefined,
    communityOid: communityFilter || undefined,
    search: searchQuery || undefined,
  });

  const resolveReport = useResolveReport();
  const dismissReport = useDismissReport();
  const bulkDismiss = useBulkDismissReports();
  const bulkResolve = useBulkResolveReports();

  // Get selectable reports (pending or reviewing)
  const selectableReports = (data?.reports || []).filter(
    (r) => r.status === 'pending' || r.status === 'reviewing'
  );

  const handleSelectReport = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedIds.length === selectableReports.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(selectableReports.map((r) => r._id));
    }
  };

  const handleBulkDismiss = async () => {
    if (selectedIds.length === 0) return;
    try {
      await bulkDismiss.mutateAsync({
        reportIds: selectedIds,
        reason: resolution || 'Bulk dismissed',
      });
      setSelectedIds([]);
      setBulkActionType(null);
      setResolution('');
      refetch();
    } catch (err) {
      // Error handled by hook
    }
  };

  const handleBulkResolve = async () => {
    if (selectedIds.length === 0) return;
    try {
      await bulkResolve.mutateAsync({
        reportIds: selectedIds,
        resolution: actionTaken,
        resolutionNotes: resolution || `Bulk resolved: ${actionTaken}`,
      });
      setSelectedIds([]);
      setBulkActionType(null);
      setResolution('');
      refetch();
    } catch (err) {
      // Error handled by hook
    }
  };

  const handleFilterChange = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value && value !== 'all') {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
  };

  const handleAction = (action, report) => {
    setSelectedReport(report);
    setActionType(action);
    setResolution('');
    setActionTaken('warning_issued');
  };

  const closeDialog = () => {
    setSelectedReport(null);
    setActionType(null);
    setResolution('');
  };

  const executeAction = async () => {
    if (!selectedReport) return;

    try {
      if (actionType === 'resolve') {
        await resolveReport.mutateAsync({
          reportId: selectedReport.reportId,
          resolution,
          actionTaken,
        });
        toast({ title: 'Report resolved successfully' });
      } else if (actionType === 'dismiss') {
        await dismissReport.mutateAsync({
          reportId: selectedReport.reportId,
          reason: resolution,
        });
        toast({ title: 'Report dismissed' });
      }
      closeDialog();
      refetch();
    } catch (err) {
      toast({
        title: 'Action failed',
        description: err.message,
        variant: 'destructive',
      });
    }
  };

  const isActionLoading = resolveReport.isPending || dismissReport.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <MessageSquareWarning className="h-6 w-6" />
          Reports Queue
        </h1>
        <p className="text-zinc-400">Review and process user reports</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="border border-yellow-500/20 bg-yellow-500/5">
          <CardContent className="p-4">
            <p className="text-sm text-yellow-400">Pending</p>
            <p className="text-2xl font-bold text-white">
              {data?.stats?.pending || 0}
            </p>
          </CardContent>
        </Card>
        <Card className="border border-blue-500/20 bg-blue-500/5">
          <CardContent className="p-4">
            <p className="text-sm text-blue-400">Reviewing</p>
            <p className="text-2xl font-bold text-white">
              {data?.stats?.reviewing || 0}
            </p>
          </CardContent>
        </Card>
        <Card className="border border-green-500/20 bg-green-500/5">
          <CardContent className="p-4">
            <p className="text-sm text-green-400">Resolved</p>
            <p className="text-2xl font-bold text-white">
              {data?.stats?.resolved || 0}
            </p>
          </CardContent>
        </Card>
        <Card className="border border-zinc-500/20 bg-zinc-500/5">
          <CardContent className="p-4">
            <p className="text-sm text-zinc-400">Dismissed</p>
            <p className="text-2xl font-bold text-white">
              {data?.stats?.dismissed || 0}
            </p>
          </CardContent>
        </Card>
        <Card className="border border-purple-500/20 bg-purple-500/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-purple-400" />
              <p className="text-sm text-purple-400">Resolution Rate</p>
            </div>
            <p className="text-2xl font-bold text-white">
              {data?.stats?.resolutionRate || 0}%
            </p>
          </CardContent>
        </Card>
        <Card className="border border-cyan-500/20 bg-cyan-500/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Timer className="h-4 w-4 text-cyan-400" />
              <p className="text-sm text-cyan-400">Avg Resolution</p>
            </div>
            <p className="text-2xl font-bold text-white">
              {data?.stats?.avgResolutionTime || 0}h
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Priority Breakdown */}
      {data?.stats?.byPriority && (
        <div className="flex flex-wrap gap-2">
          <span className="text-xs text-zinc-500 mr-2">By Priority:</span>
          <Badge variant="outline" className="bg-zinc-500/10 text-zinc-400 border-zinc-500/30">
            Low: {data.stats.byPriority.low || 0}
          </Badge>
          <Badge variant="outline" className="bg-yellow-500/10 text-yellow-400 border-yellow-500/30">
            Medium: {data.stats.byPriority.medium || 0}
          </Badge>
          <Badge variant="outline" className="bg-orange-500/10 text-orange-400 border-orange-500/30">
            High: {data.stats.byPriority.high || 0}
          </Badge>
          <Badge variant="outline" className="bg-red-500/10 text-red-400 border-red-500/30">
            Critical: {data.stats.byPriority.critical || 0}
          </Badge>
        </div>
      )}

      {/* Filters */}
      <Card className="border border-zinc-800 bg-zinc-900/50">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            {/* Search and Filters Row */}
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                <Input
                  placeholder="Search reports..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                />
              </div>

              <Select
                value={statusFilter}
                onValueChange={(v) => handleFilterChange('status', v)}
              >
                <SelectTrigger className="w-full md:w-[150px] bg-zinc-800 border-zinc-700 text-white">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="reviewing">Reviewing</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="dismissed">Dismissed</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={reasonFilter}
                onValueChange={(v) => handleFilterChange('reason', v)}
              >
                <SelectTrigger className="w-full md:w-[150px] bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Reason" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all">All Reasons</SelectItem>
                  <SelectItem value="spam">Spam</SelectItem>
                  <SelectItem value="harassment">Harassment</SelectItem>
                  <SelectItem value="hate_speech">Hate Speech</SelectItem>
                  <SelectItem value="misinformation">Misinformation</SelectItem>
                  <SelectItem value="inappropriate">Inappropriate</SelectItem>
                  <SelectItem value="copyright">Copyright</SelectItem>
                  <SelectItem value="scam">Scam</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={priorityFilter}
                onValueChange={(v) => handleFilterChange('priority', v)}
              >
                <SelectTrigger className="w-full md:w-[150px] bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Bulk Actions Row */}
            {selectableReports.length > 0 && (
              <div className="flex items-center gap-4 pt-2 border-t border-zinc-800">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSelectAll}
                  className="border-zinc-700 bg-zinc-800 hover:bg-zinc-700 text-white"
                >
                  {selectedIds.length === selectableReports.length ? (
                    <>
                      <CheckSquare className="h-4 w-4 mr-2" />
                      Deselect All
                    </>
                  ) : (
                    <>
                      <Square className="h-4 w-4 mr-2" />
                      Select All ({selectableReports.length})
                    </>
                  )}
                </Button>

                {selectedIds.length > 0 && (
                  <>
                    <span className="text-sm text-zinc-400">
                      {selectedIds.length} selected
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setBulkActionType('dismiss')}
                      className="border-zinc-700 bg-zinc-800 hover:bg-zinc-700 text-zinc-400"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Dismiss Selected
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => setBulkActionType('resolve')}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Resolve Selected
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Reports List */}
      <Card className="border border-zinc-800 bg-zinc-900/50 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-zinc-400">Loading reports...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <AlertTriangle className="h-8 w-8 text-red-400 mx-auto mb-4" />
            <p className="text-red-400">{error.message}</p>
          </div>
        ) : data?.reports?.length === 0 ? (
          <div className="p-8 text-center">
            <MessageSquareWarning className="h-8 w-8 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">No reports found</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-zinc-800">
              {data?.reports?.map((report) => (
                <ReportRow
                  key={report.reportId}
                  report={report}
                  onAction={handleAction}
                  isSelected={selectedIds.includes(report._id)}
                  onSelect={handleSelectReport}
                />
              ))}
            </div>

            {/* Pagination */}
            {data?.pagination && (
              <div className="p-4 border-t border-zinc-800 flex items-center justify-between">
                <p className="text-sm text-zinc-400">
                  Showing {(currentPage - 1) * 20 + 1} to{' '}
                  {Math.min(currentPage * 20, data.pagination.total)} of{' '}
                  {data.pagination.total} reports
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="border-zinc-700 bg-zinc-800 hover:bg-zinc-700 text-white"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="px-3 py-1 text-sm text-zinc-400 flex items-center">
                    Page {currentPage} of {data.pagination.pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= data.pagination.pages}
                    className="border-zinc-700 bg-zinc-800 hover:bg-zinc-700 text-white"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* View Report Details Dialog */}
      <Dialog open={actionType === 'view'} onOpenChange={() => closeDialog()}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-blue-400" />
              Report Details
            </DialogTitle>
          </DialogHeader>
          {selectedReport && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-zinc-500">Report ID</label>
                  <p className="text-sm text-white">
                    {selectedReport.reportId}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-zinc-500">Status</label>
                  <p className="text-sm text-white capitalize">
                    {selectedReport.status}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-zinc-500">Priority</label>
                  <p className="text-sm text-white capitalize">
                    {selectedReport.priority}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-zinc-500">Reason</label>
                  <p className="text-sm text-white">
                    {REPORT_REASONS[selectedReport.reason]?.label ||
                      selectedReport.reason}
                  </p>
                </div>
              </div>

              <div>
                <label className="text-xs text-zinc-500">Reporter</label>
                <p className="text-sm text-white font-mono">
                  {selectedReport.reporterWallet}
                </p>
              </div>

              <div>
                <label className="text-xs text-zinc-500">Reported Entity</label>
                <p className="text-sm text-white">
                  <span className="capitalize">
                    {selectedReport.entityType}
                  </span>
                  : {selectedReport.entityId}
                </p>
              </div>

              {selectedReport.details && (
                <div>
                  <label className="text-xs text-zinc-500">Details</label>
                  <p className="text-sm text-white bg-zinc-800 p-3 rounded-lg mt-1">
                    {selectedReport.details}
                  </p>
                </div>
              )}

              {selectedReport.contentSnapshot && (
                <div>
                  <label className="text-xs text-zinc-500">
                    Content Snapshot
                  </label>
                  <div className="bg-zinc-800 p-3 rounded-lg mt-1">
                    {selectedReport.contentSnapshot.title && (
                      <p className="text-white font-medium">
                        {selectedReport.contentSnapshot.title}
                      </p>
                    )}
                    {selectedReport.contentSnapshot.content && (
                      <p className="text-sm text-zinc-400 mt-1 line-clamp-4">
                        {selectedReport.contentSnapshot.content}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {selectedReport.resolution && (
                <div>
                  <label className="text-xs text-zinc-500">Resolution</label>
                  <div className="bg-zinc-800 p-3 rounded-lg mt-1">
                    <p className="text-sm text-white">
                      {selectedReport.resolution.notes}
                    </p>
                    <p className="text-xs text-zinc-500 mt-2">
                      Resolved by: {selectedReport.resolution.resolvedBy} on{' '}
                      {new Date(
                        selectedReport.resolution.resolvedAt
                      ).toLocaleString()}
                    </p>
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs text-zinc-500">Reported At</label>
                <p className="text-sm text-white">
                  {new Date(selectedReport.createdAt).toLocaleString()}
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={closeDialog}
              className="text-zinc-400 hover:text-white"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resolve Report Dialog */}
      <Dialog
        open={actionType === 'resolve'}
        onOpenChange={() => closeDialog()}
      >
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-400" />
              Resolve Report
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Mark this report as resolved and document the action taken.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-zinc-400">Action Taken</label>
              <Select value={actionTaken} onValueChange={setActionTaken}>
                <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="warning_issued">Warning Issued</SelectItem>
                  <SelectItem value="content_removed">
                    Content Removed
                  </SelectItem>
                  <SelectItem value="content_hidden">Content Hidden</SelectItem>
                  <SelectItem value="user_suspended">User Suspended</SelectItem>
                  <SelectItem value="user_banned">User Banned</SelectItem>
                  <SelectItem value="no_action_needed">
                    No Action Needed
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-zinc-400">Resolution Notes</label>
              <Textarea
                placeholder="Document your resolution..."
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                className="mt-1 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={closeDialog}
              className="text-zinc-400 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={executeAction}
              disabled={!resolution || isActionLoading}
              className="bg-green-500 hover:bg-green-600 text-black"
            >
              {isActionLoading ? 'Processing...' : 'Resolve Report'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dismiss Report Dialog */}
      <Dialog
        open={actionType === 'dismiss'}
        onOpenChange={() => closeDialog()}
      >
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-zinc-400" />
              Dismiss Report
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Dismiss this report as invalid or not requiring action.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-zinc-400">
                Reason for Dismissal
              </label>
              <Textarea
                placeholder="Explain why this report is being dismissed..."
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                className="mt-1 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={closeDialog}
              className="text-zinc-400 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={executeAction}
              disabled={!resolution || isActionLoading}
              variant="secondary"
              className="bg-zinc-700 hover:bg-zinc-600 text-white"
            >
              {isActionLoading ? 'Processing...' : 'Dismiss Report'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Dismiss Dialog */}
      <Dialog
        open={bulkActionType === 'dismiss'}
        onOpenChange={() => {
          setBulkActionType(null);
          setResolution('');
        }}
      >
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-zinc-400" />
              Bulk Dismiss Reports
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Dismiss {selectedIds.length} selected reports as invalid or not
              requiring action.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-zinc-400">
                Reason for Dismissal (Optional)
              </label>
              <Textarea
                placeholder="Explain why these reports are being dismissed..."
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                className="mt-1 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setBulkActionType(null);
                setResolution('');
              }}
              className="text-zinc-400 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkDismiss}
              disabled={bulkDismiss.isPending}
              variant="secondary"
              className="bg-zinc-700 hover:bg-zinc-600 text-white"
            >
              {bulkDismiss.isPending
                ? 'Processing...'
                : `Dismiss ${selectedIds.length} Reports`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Resolve Dialog */}
      <Dialog
        open={bulkActionType === 'resolve'}
        onOpenChange={() => {
          setBulkActionType(null);
          setResolution('');
        }}
      >
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-400" />
              Bulk Resolve Reports
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Resolve {selectedIds.length} selected reports with the same action.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-zinc-400">Action Taken</label>
              <Select value={actionTaken} onValueChange={setActionTaken}>
                <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="warning_issued">Warning Issued</SelectItem>
                  <SelectItem value="content_removed">Content Removed</SelectItem>
                  <SelectItem value="content_hidden">Content Hidden</SelectItem>
                  <SelectItem value="user_suspended">User Suspended</SelectItem>
                  <SelectItem value="user_banned">User Banned</SelectItem>
                  <SelectItem value="no_action_needed">No Action Needed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-zinc-400">
                Resolution Notes (Optional)
              </label>
              <Textarea
                placeholder="Document your resolution..."
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                className="mt-1 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setBulkActionType(null);
                setResolution('');
              }}
              className="text-zinc-400 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkResolve}
              disabled={bulkResolve.isPending}
              className="bg-green-500 hover:bg-green-600 text-black"
            >
              {bulkResolve.isPending
                ? 'Processing...'
                : `Resolve ${selectedIds.length} Reports`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
